import {
  Sun,
  Activity,
  BarChart3,
  GraduationCap,
  TrendingUp,
  CalendarDays,
  CalendarClock,
  Compass,
  Star,
  ScrollText,
  Gauge,
  Stethoscope,
  Users,
  Brain,
  Radar as RadarIcon,
  Fingerprint,
  IdCard,
  Waves,
  Share2,
  LineChart,
  BarChart2,
  Trophy,
  ListFilter,
  MessageSquareText,
  ScatterChart,
  ListChecks,
  GitCompare,
  Bell,
  Briefcase,
  Heart,
  Orbit,
  History,
  Bot,
  Layers,
  CalendarRange,
  Swords,
  Medal,
  Eye,
  EyeOff,
  Gift,
  Shield,
  ClipboardList,
  Blocks,
  ShieldCheck,
  Radio,
} from 'lucide-react';

// Single source of truth for sidebar + command palette navigation.
// premium: true items are wrapped by PremiumRoute/SubscriptionGate in App.js —
// the flag here only drives a visual lock affordance, not the actual gate.
//
// IA redesign (2026-07-02, ESME x 21-ERT COUNCIL CPO finding + Commander
// decision): the original 6 groups had an 18-item "Premium Analysis" bucket
// that was really a junk drawer — max single-group cognitive load, not a
// taxonomy. Regrouped into 5 folders using the CEIM Output-Envelope pattern,
// keyed to WHAT KIND OF COGNITIVE ACT each feature asks of the user, not
// which team built it or whether it's gated:
//   Briefs    — ambient/low-effort reads, no active querying
//   Signals   — a specific, live, tradeable claim right now
//   Models    — tools you actively query to build/test a view
//   Scenarios — simulation, forward-looking, competitive/what-if
//   Doctrine  — your own sealed record and self-governance
// This caps any one folder at 11 items (down from 18). The worst
// near-duplicate pair (Screener vs Smart Screener) was resolved separately
// on 2026-07-02 by merging both into one tabbed page at /screener.
// SRF stays outside all 5 — it's a separate B2B product, not a trading
// cognitive act, and forcing it into any bucket would be confusing.

export const NAV_GROUPS = [
  {
    group: 'Briefs',
    items: [
      { label: 'Resonance', path: 'https://gammaqc.com/resonance', icon: Radio, premium: false, external: true },
      { label: 'Daily Digest', path: '/daily-digest', icon: Sun, premium: false },
      { label: 'Weekly Digest', path: '/weekly-digest', icon: CalendarRange, premium: true },
      { label: 'Your Brief', path: '/me/brief', icon: ScrollText, premium: false },
      { label: 'Discover', path: '/discover', icon: Compass, premium: false },
      { label: 'Unusual Activity', path: '/meme-stocks', icon: Activity, premium: false },
      { label: 'Sector Heatmap', path: '/sector-heatmap', icon: BarChart3, premium: false },
      { label: 'Earnings Foresight', path: '/earnings-calendar', icon: CalendarDays, premium: false },
      { label: 'Foresight Scoreboard', path: '/foresight-scoreboard', icon: EyeOff, premium: false },
    ],
  },
  {
    group: 'Signals',
    items: [
      { label: 'Chart Vision', path: '/chart-vision', icon: LineChart, premium: false },
      { label: 'Signal Fusion', path: '/signal-fusion', icon: Layers, premium: true },
      { label: 'Options Flow', path: '/options-flow', icon: Waves, premium: true },
      { label: 'Sentiment', path: '/sentiment', icon: Heart, premium: true },
      { label: 'Top Picks', path: '/top-picks', icon: Trophy, premium: true },
      { label: 'Alerts', path: '/alerts', icon: Bell, premium: true },
      { label: 'Social', path: '/social', icon: Share2, premium: true },
      { label: 'Watchlist', path: '/watchlist', icon: Star, premium: true },
    ],
  },
  {
    group: 'Models',
    items: [
      { label: 'Stock Analysis', path: '/analysis', icon: BarChart2, premium: true },
      { label: 'Strategy Analyzer', path: '/strategy-analyzer', icon: ScatterChart, premium: true },
      { label: 'Screener', path: '/screener', icon: ListFilter, premium: true },
      { label: 'Compare', path: '/compare', icon: GitCompare, premium: true },
      { label: 'Backtest', path: '/backtest', icon: History, premium: true },
      { label: 'AI Assistant', path: '/ai-assistant', icon: Bot, premium: true },
      { label: 'Portfolio', path: '/portfolio', icon: Briefcase, premium: true },
      { label: 'Learning', path: '/learning', icon: GraduationCap, premium: false },
      { label: 'Progress', path: '/progress', icon: TrendingUp, premium: false },
    ],
  },
  {
    group: 'Scenarios',
    items: [
      { label: 'Collision Matrix', path: '/collision', icon: Blocks, premium: false },
      { label: 'Trade Replay', path: '/trade-replay', icon: History, premium: true },
      { label: 'Strategy Map', path: '/strategy-map', icon: Blocks, premium: true },
      { label: 'Paper Trading', path: '/paper-trading', icon: ListChecks, premium: false },
      { label: 'Tournaments', path: '/tournaments', icon: Swords, premium: false },
      { label: 'Battle Leaderboard', path: '/leaderboard', icon: Medal, premium: false },
    ],
  },
  {
    group: 'Doctrine',
    items: [
      { label: 'Thesis Archive', path: '/ledger', icon: ClipboardList, premium: false },
      { label: 'Mint Thesis', path: '/ledger/new', icon: ShieldCheck, premium: false },
      { label: 'Scorecard', path: '/me/scorecard', icon: Gauge, premium: false },
      { label: 'Calibration', path: '/me/calibration', icon: Fingerprint, premium: false },
      { label: 'Autopsy', path: '/me/autopsy', icon: Stethoscope, premium: false },
      { label: 'Source Verdict', path: '/me/source-verdict', icon: Users, premium: false },
      { label: 'Biases', path: '/me/biases', icon: Brain, premium: false },
      { label: 'Radar', path: '/me/radar', icon: RadarIcon, premium: false },
      { label: 'Digital Twin', path: '/me/twin', icon: Bot, premium: false },
      { label: 'Trader Card', path: '/me/trader-card', icon: IdCard, premium: false },
      { label: 'Referral', path: '/referral', icon: Gift, premium: false },
    ],
  },
  {
    group: 'SRF',
    items: [
      { label: 'Sovereign Review Fabric', path: '/srf', icon: Shield, premium: false },
    ],
  },
];

// Flattened list for the command palette (same source, different shape).
export const NAV_FLAT = NAV_GROUPS.flatMap((g) =>
  g.items.map((item) => ({ ...item, group: g.group }))
);
